import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('checking');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else {
            navigate('/cart');
        }
    }, [searchParams]);

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 5;
        
        if (attempts >= maxAttempts) {
            setStatus('timeout');
            toast.error('Payment verification timed out. Please check your email for confirmation.');
            return;
        }

        try {
            const response = await axios.get(`${API}/checkout/status/${sessionId}`);
            
            if (response.data.payment_status === 'paid') {
                setStatus('success');
                setPaymentInfo(response.data);
                toast.success('Payment successful!');
            } else if (response.data.status === 'expired') {
                setStatus('expired');
                toast.error('Payment session expired');
            } else {
                // Continue polling
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            setStatus('error');
            toast.error('Failed to verify payment');
        }
    };

    if (status === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16" data-testid="checking-status">
                <div className="text-center">
                    <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
                    <p className="text-xl text-muted-foreground">Verifying your payment...</p>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16" data-testid="success-status">
                <div className="text-center max-w-2xl px-6">
                    <CheckCircle className="w-24 h-24 mx-auto mb-6 text-green-500" strokeWidth={1.5} />
                    <h1 className="text-5xl md:text-6xl font-bold tracking-tighter mb-4">Payment Successful!</h1>
                    <p className="text-xl text-muted-foreground mb-8">
                        Thank you for your purchase. Your digital products are now available in your dashboard.
                    </p>
                    {paymentInfo && (
                        <div className="bg-card border border-border p-6 mb-8 text-left" data-testid="payment-info">
                            <h3 className="font-semibold mb-4">Payment Details</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Amount:</span>
                                    <span>${paymentInfo.amount}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Currency:</span>
                                    <span>{paymentInfo.currency.toUpperCase()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Status:</span>
                                    <span className="text-green-500">Paid</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex gap-4 justify-center">
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="btn-primary"
                            data-testid="view-purchases-button"
                        >
                            View My Purchases
                        </button>
                        <button 
                            onClick={() => navigate('/browse')}
                            className="btn-secondary"
                            data-testid="continue-shopping-button"
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center pt-16" data-testid="error-status">
            <div className="text-center">
                <p className="text-xl text-muted-foreground mb-6">
                    {status === 'timeout' && 'Payment verification timed out'}
                    {status === 'expired' && 'Payment session expired'}
                    {status === 'error' && 'Failed to verify payment'}
                </p>
                <button 
                    onClick={() => navigate('/cart')}
                    className="btn-primary"
                >
                    Return to Cart
                </button>
            </div>
        </div>
    );
};

export default CheckoutSuccess;
